import getRadiologistDash from '@custom/rtk-apis/analytics/getRadiologistDash';
import { AppDispatch, RootState } from '@custom/store/store';
import { Loader } from '@ohif/ui';
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { downloadIconBlack, Excel } from '../../ui/assets/svgIcons/icon';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { servicesManager } from '../../app/src/App';

const RadiologistTable = ({ selectedYear }) => {
  const { uiNotificationService } = servicesManager.services;

  const dispatch = useDispatch<AppDispatch>();
  const tableData = useSelector((state: RootState) => state.getRadiologistDashResponse)
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const tableRef = useRef(null);


  const tabs = ['Modality Cases', 'Modality TAT', 'Priority TAT', 'Report Completion', 'Priority Report Distribution', 'Cases'];

  const fetchTableData = async () => {
    setIsLoading(true);
    const query = `?startDate=${selectedYear}-01-01&endDate=${selectedYear}-12-31`
    const radiologistDashResponse = await dispatch(getRadiologistDash(query)).unwrap();
    setIsLoading(false);
  }

  const handleDownloadTable = async (name: string) => {
    const chartElement = tableRef.current;
    if (!chartElement) return;

    const downloadButton = chartElement.querySelectorAll('.btn__white');
    if (downloadButton) downloadButton.forEach(button => (button.style.display = 'none'));

    const canvas = await html2canvas(chartElement);
    const imgData = canvas.toDataURL('image/png');

    const pdf = new jsPDF('portrait');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    pdf.setFontSize(16);
    pdf.text(`${name} - ${selectedYear}`, 10, 15);

    // Add the table image below the title
    pdf.addImage(imgData, 'PNG', 10, 20, pdfWidth - 20, pdfHeight);

    // Show the button again after capturing
    if (downloadButton) downloadButton.forEach(button => (button.style.display = 'block'));

    pdf.save(`${name} ${selectedYear}.pdf`);
  };

  const handleDownloadExcel = async () => {
    if (!tableData?.result) return;

    let data = [];
    let sheetName = "";
    let columns = [];

    switch (activeTab) {
      case 0: // Modality Cases
        sheetName = "Modality Cases";
        data = tableData.result.modalityCounts.map((item, index) => ({
          "S.No.": index + 1,
          "Modality": item.name || '--',
          "No of Cases": item.count || '--',
        }));
        columns = ["S.No.", "Modality", "No of Cases"];
        break;

      case 1: // Modality Turnaround Time
        sheetName = "Modality TAT";
        data = tableData.result.aggregatedAnalytics.map((item, index) => ({
          "S.No.": index + 1,
          "Modality": item.modality || '--',
          "Average TAT": item.averageTAT || '--',
          "No of Cases": item.count || '--',
        }));
        columns = ["S.No.", "Modality", "Average TAT", "No of Cases"];
        break;

      case 2: // Priority Turnaround Time
        sheetName = "Priority TAT";
        data = tableData.result.detailedAnalytics.map((item, index) => ({
          "S.No.": index + 1,
          "Report Name": item.reportName || '--',
          "Priority": item.priority || '--',
          "Priority TAT": item.priorityTurnaroundTime || '--',
          "Status": item.status || '--',
          "Modality": item.modality || '--',
        }));
        columns = ["S.No.", "Report Name", "Priority", "Priority TAT", "Status", "Modality"];
        break;

      case 3: // Report Completion Metrics
        sheetName = "Report Completion";
        data = tableData.result.statusCounts.map((item, index) => ({
          "S.No.": index + 1,
          "Status": item.name || '--',
          "No of Reports": item.count || '--',
        }));
        columns = ["S.No.", "Status", "No of Reports"];
        break;

      case 4: // Report Status Distribution Priority
        sheetName = "Report Status Distribution";
        data = tableData.result.priorityCounts.map((item, index) => ({
          "S.No.": index + 1,
          "Status": item.name || '--',
          "No of Reports": item.count || '--',
        }));
        columns = ["S.No.", "Status", "No of Reports"];
        break;

      case 5: // Number of Cases Per Month
        sheetName = "Monthly Cases";
        data = tableData.result.monthlyReports.map((item, index) => ({
          "S.No.": index + 1,
          "Months": item.month || '--',
          "No of Cases": item.count || '--',
        }));
        columns = ["S.No.", "Months", "No of Cases"];
        break;

      default:
        return;
    }

    if (data.length === 0) {
      uiNotificationService.show({
        title: 'Unable to download Excel',
        message: "No data found to export excel",
        type: 'error',
      });
      return;
    }

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet(sheetName);

    worksheet.columns = columns.map((key) => ({
      header: key,
      key,
      width: Math.max(20, key.length + 5), // Dynamic width
    }));

    worksheet.getRow(1).font = { bold: true };
    worksheet.getRow(1).alignment = { horizontal: 'center' };

    data.forEach((row) => worksheet.addRow(row));

    worksheet.eachRow((row) => {
      row.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `${sheetName}_Report ${selectedYear}.xlsx`);
  };


  useEffect(() => {
    fetchTableData();
  }, [selectedYear])
  return (
    <div>
      <div className="tabs-container">
        <div className="tabs-header">
          {tabs.map((tab, index) => (
            <button
              key={index}
              className={`tab ${activeTab === index ? "active" : ""}`}
              onClick={() => setActiveTab(index)}
            >
              {tab}
            </button>
          ))}
        </div>
        <div className="tabs-content">
          {
            activeTab == 0 && <div className='tableWrap' ref={tableRef}>
              <div className='one-line'>
                <p style={{ fontSize: '20px', fontWeight: '500' }}>Modality Cases</p>
                <div className='flex gap-[10px]'>
                  <button className='btn btn__white btn--sm' onClick={() => handleDownloadTable("Modality Tabular Report")}>
                    <img className='w-[30px]' src={downloadIconBlack} alt="icon" />
                  </button>
                  <button className='btn btn__white btn--sm' onClick={handleDownloadExcel}>
                    <img className='w-[30px]' src={Excel} alt='Excel icon' />
                  </button>
                </div>
              </div>
              <div className='tableContainer'>
                <div className='tableContainer__wrap'>
                  <table className='table'>
                    <thead>
                      <th>S.No.</th>
                      <th>Modality</th>
                      <th>No of Cases</th>
                    </thead>
                    <tbody>
                      {tableData?.result?.modalityCounts.map((item, index) => {
                        return <tr>
                          <td>{index + 1}</td>
                          <td>{item.name || '--'}</td>
                          <td>{item.count || '--'}</td>
                        </tr>
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          }
          {
            activeTab == 1 && <div className='tableWrap' ref={tableRef}>
              <div className='one-line'>
                <p style={{ fontSize: '20px', fontWeight: '500' }}>Modality Turnaround Time</p>
                <div className='flex gap-[10px]'>
                  <button className='btn btn__white btn--sm' onClick={() => handleDownloadTable("Modality TAT Tabular Report")}>
                    <img className='w-[30px]' src={downloadIconBlack} alt="icon" />
                  </button>
                  <button className='btn btn__white btn--sm' onClick={handleDownloadExcel}>
                    <img src={Excel} alt="Excel icon" />
                  </button>
                </div>
              </div>
              <div className='tableContainer'>
                <div className='tableContainer__wrap'>
                  <table className='table'>
                    <thead>
                      <th>S.No.</th>
                      <th>Modality</th>
                      <th>Average TAT</th>
                      <th>No of Cases</th>
                    </thead>
                    <tbody>
                      {tableData?.result?.aggregatedAnalytics.map((item, index) => {
                        return <tr>
                          <td>{index + 1}</td>
                          <td>{item.modality || '--'}</td>
                          <td>{item.averageTAT || '--'}</td>
                          <td>{item.count || '--'}</td>
                        </tr>
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          }
          {
            activeTab == 2 && <div className='tableWrap' ref={tableRef}>
              <div className='one-line'>
                <p style={{ fontSize: '20px', fontWeight: '500' }}>Priority Turnaround Time (TAT)</p>
                <div className='flex gap-[10px]'>
                  <button className='btn btn__white btn--sm' onClick={() => handleDownloadTable("TAT Tabular Report")}>
                    <img className='w-[30px]' src={downloadIconBlack} alt="icon" />
                  </button>
                  <button className='btn btn__white btn--sm' onClick={handleDownloadExcel}>
                    <img src={Excel} alt="Excel icon" />
                  </button>
                </div>
              </div>
              <div className='tableContainer'>
                <div className='tableContainer__wrap'>
                  <table className='table'>
                    <thead>
                      <th>S.No.</th>
                      <th>Report Name</th>
                      <th>Priority</th>
                      <th>Priority TAT</th>
                      <th>Status</th>
                      <th>Modality</th>
                    </thead>
                    <tbody>
                      {tableData?.result?.detailedAnalytics.map((item, index) => {
                        return <tr>
                          <td>{index + 1}</td>
                          <td>{item.reportName || '--'}</td>
                          <td>{item.priority || '--'}</td>
                          <td>{item.priorityTurnaroundTime || '--'}</td>
                          <td>{item.status || '--'}</td>
                          <td>{item.modality || '--'}</td>
                        </tr>
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          }
          {
            activeTab == 3 && <div className='tableWrap' ref={tableRef}>
              <div className='one-line'>
                <p style={{ fontSize: '20px', fontWeight: '500' }}>Report Completion Metrics</p>
                <div className='flex gap-[10px]'>
                  <button className='btn btn__white btn--sm' onClick={() => handleDownloadTable("Report Completion Tabular Report")}>
                    <img className='w-[30px]' src={downloadIconBlack} alt="icon" />
                  </button>
                  <button className='btn btn__white btn--sm' onClick={handleDownloadExcel}>
                    <img src={Excel} alt="Excel icon" />
                  </button>
                </div>
              </div>              <div className='tableContainer'>
                <div className='tableContainer__wrap'>
                  <table className='table'>
                    <thead>
                      <th>S.No.</th>
                      <th>Status</th>
                      <th>No of Reports</th>
                    </thead>
                    <tbody>
                      {tableData?.result?.statusCounts.map((item, index) => {
                        return <tr>
                          <td>{index + 1}</td>
                          <td>{item.name || '--'}</td>
                          <td>{item.count || '--'}</td>
                        </tr>
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          }
          {
            activeTab == 4 && <div className='tableWrap' ref={tableRef}>
              <div className='one-line'>
                <p style={{ fontSize: '20px', fontWeight: '500' }}>Report Status Distribution Priority</p>
                <div className='flex gap-[10px]'>
                  <button className='btn btn__white btn--sm' onClick={() => handleDownloadTable("Report Status Tabular Report")}>
                    <img className='w-[30px]' src={downloadIconBlack} alt="icon" />
                  </button>
                  <button className='btn btn__white btn--sm' onClick={handleDownloadExcel}>
                    <img src={Excel} alt="icon" />
                  </button>
                </div>
              </div>
              <div className='tableContainer'>
                <div className='tableContainer__wrap'>
                  <table className='table'>
                    <thead>
                      <th>S.No.</th>
                      <th>Status</th>
                      <th>No of Reports</th>
                    </thead>
                    <tbody>
                      {tableData?.result?.priorityCounts.map((item, index) => {
                        return <tr>
                          <td>{index + 1}</td>
                          <td>{item.name || '--'}</td>
                          <td>{item.count || '--'}</td>
                        </tr>
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          }
          {
            activeTab == 5 && <div className='tableWrap' ref={tableRef}>
              <div className='one-line'>
                <p style={{ fontSize: '20px', fontWeight: '500' }}>Number of cases</p>
                <div className='flex gap-[10px]'>
                  <button className='btn btn__white btn--sm' onClick={() => handleDownloadTable("cases Tabular Report")}>
                    <img className='w-[30px]' src={downloadIconBlack} alt="icon" />
                  </button>
                  <button className='btn btn__white btn--sm' onClick={handleDownloadExcel}>
                    <img src={Excel} alt="Excel icon" />
                  </button>
                </div>
              </div>              <div className='tableContainer'>
                <div className='tableContainer__wrap'>
                  <table className='table'>
                    <thead>
                      <th>S.No.</th>
                      <th>Months</th>
                      <th>No of cases</th>
                    </thead>
                    <tbody>
                      {tableData?.result?.monthlyReports.map((item, index) => {
                        return <tr>
                          <td>{index + 1}</td>
                          <td>{item.month || '--'}</td>
                          <td>{item.count || '--'}</td>
                        </tr>
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          }
        </div>
      </div>
      {<Loader isLoaderOpen={isLoading} />}
    </div>
  )
}

export default RadiologistTable
